/**
 * FikoGCodeGenerator - Profesyonel G-Code Üretici
 *
 * Desteklenen CNC Kontrol Üniteleri:
 * - Fanuc (ISO)
 * - Haas
 * - Siemens (840D)
 * - Heidenhain
 * - Mach3/Mach4
 * - LinuxCNC/GRBL
 *
 * @author FikoYT
 * @version 1.0.0
 */

import { FikoVektorTakimYol3D } from './FikoVektorTakimYol3d';
import { FikoTakimYolu } from './FikoTakimYolu';

/**
 * Post-Processor Konfigürasyonu
 */
export interface FikoPostProcessorConfig {
    /** Post-processor ismi */
    isim: string;
    /** Dosya uzantısı (.nc, .ngc, .tap, .cnc vb.) */
    uzanti: string;
    /** Ondalık hassasiyet */
    hassasiyet: number;
    /** Başlangıç bloğu */
    baslatmaKodu: string[];
    /** Bitiş bloğu */
    bitisKodu: string[];
    /** Satır numarası başlangıç */
    satirNumarasiBaslangic: number;
    /** Satır numarası artış */
    satirNumarasiArtis: number;
    /** Satır numarası kullan */
    satirNumarasiKullan: boolean;
    /** % başlık kullan */
    yuzdeBaslik: boolean;
    /** Program numarası */
    programNumarasi: number;
    /** Spindle komutu formatı */
    spindleFormat: 'M3S{rpm}' | 'S{rpm}M3' | 'S{rpm} M3';
    /** Modal G kodları kullan (tekrarlama, G0/G1/G2/G3) */
    modalKullan: boolean;
    /** Absolute/Incremental */
    absoluteMode: boolean;
    /** XYZ formatı */
    koordinatFormat: 'X{x}Y{y}Z{z}' | 'X{x} Y{y} Z{z}';
    /** Feed rate formatı */
    feedFormat: 'F{feed}' | 'F{feed:d}' | 'F{feed:.0f}' | 'F{feed:.1f}';
    /** Comment format */
    yorumFormat: '({yorum})' | ';{yorum}' | '/*{yorum}*/';
    /** Coolant komutları */
    coolantOn: string;
    coolantOff: string;
    /** Tool change format */
    toolChangeFormat: string;
    /** Arc format (IJ mutlak/incremental) */
    arcFormatAbsolute: boolean;
    /** Safe Z position */
    safeZ: number;
    /** Home position */
    homeX: number;
    homeY: number;
    homeZ: number;
}

/**
 * Hazır Post-Processor Presetleri
 */
export const POST_PROCESSOR_PRESETS: Record<string, Partial<FikoPostProcessorConfig>> = {
    'Fanuc': {
        isim: 'Fanuc (ISO)',
        uzanti: '.nc',
        hassasiyet: 4,
        baslatmaKodu: ['%', 'O{programNo}', 'G90 G54 G17 G40 G49 G80', 'G21'],
        bitisKodu: ['M5', 'G91 G28 Z0', 'G28 X0 Y0', 'M30', '%'],
        satirNumarasiBaslangic: 10,
        satirNumarasiArtis: 10,
        satirNumarasiKullan: true,
        yuzdeBaslik: true,
        programNumarasi: 1,
        spindleFormat: 'S{rpm}M3',
        modalKullan: true,
        absoluteMode: true,
        koordinatFormat: 'X{x}Y{y}Z{z}',
        feedFormat: 'F{feed:.0f}',
        yorumFormat: '({yorum})',
        coolantOn: 'M8',
        coolantOff: 'M9',
        toolChangeFormat: 'T{tool}M6',
        arcFormatAbsolute: false,
        safeZ: 50,
        homeX: 0,
        homeY: 0,
        homeZ: 100
    },
    'Haas': {
        isim: 'Haas VF Series',
        uzanti: '.nc',
        hassasiyet: 4,
        baslatmaKodu: ['%', 'O{programNo} ({programAdi})', 'G00 G17 G40 G49 G80 G90', 'G21 (METRIC)', 'G54'],
        bitisKodu: ['M5', 'G53 G49 Z0', 'M30', '%'],
        satirNumarasiBaslangic: 1,
        satirNumarasiArtis: 1,
        satirNumarasiKullan: false,
        yuzdeBaslik: true,
        programNumarasi: 1001,
        spindleFormat: 'S{rpm} M3',
        modalKullan: true,
        absoluteMode: true,
        koordinatFormat: 'X{x} Y{y} Z{z}',
        feedFormat: 'F{feed:.0f}',
        yorumFormat: '({yorum})',
        coolantOn: 'M8',
        coolantOff: 'M9',
        toolChangeFormat: 'T{tool} M6 ({takimAdi})',
        arcFormatAbsolute: false,
        safeZ: 25,
        homeX: 0,
        homeY: 0,
        homeZ: 50
    },
    'Siemens840D': {
        isim: 'Siemens 840D',
        uzanti: '.spf',
        hassasiyet: 3,
        baslatmaKodu: [';SIEMENS 840D', 'G90 G54 G17 G64', 'G71'],
        bitisKodu: ['M5', 'G0 Z200', 'M30'],
        satirNumarasiBaslangic: 10,
        satirNumarasiArtis: 10,
        satirNumarasiKullan: true,
        yuzdeBaslik: false,
        programNumarasi: 0,
        spindleFormat: 'S{rpm} M3',
        modalKullan: true,
        absoluteMode: true,
        koordinatFormat: 'X{x} Y{y} Z{z}',
        feedFormat: 'F{feed}',
        yorumFormat: ';{yorum}',
        coolantOn: 'M8',
        coolantOff: 'M9',
        toolChangeFormat: 'T{tool} D1',
        arcFormatAbsolute: true,
        safeZ: 200,
        homeX: 0,
        homeY: 0,
        homeZ: 200
    },
    'Heidenhain': {
        isim: 'Heidenhain TNC',
        uzanti: '.h',
        hassasiyet: 3,
        baslatmaKodu: ['BEGIN PGM {programAdi} MM', 'BLK FORM 0.1 Z X+0 Y+0 Z-50', 'BLK FORM 0.2 X+100 Y+100 Z+0'],
        bitisKodu: ['L Z+200 R0 FMAX M5', 'END PGM {programAdi} MM'],
        satirNumarasiBaslangic: 0,
        satirNumarasiArtis: 1,
        satirNumarasiKullan: true,
        yuzdeBaslik: false,
        programNumarasi: 0,
        spindleFormat: 'S{rpm} M3',
        modalKullan: false,
        absoluteMode: true,
        koordinatFormat: 'X{x} Y{y} Z{z}',
        feedFormat: 'F{feed}',
        yorumFormat: ';{yorum}',
        coolantOn: 'M8',
        coolantOff: 'M9',
        toolChangeFormat: 'TOOL CALL {tool} Z S{rpm}',
        arcFormatAbsolute: true,
        safeZ: 200,
        homeX: 0,
        homeY: 0,
        homeZ: 200
    },
    'Mach3': {
        isim: 'Mach3/Mach4',
        uzanti: '.tap',
        hassasiyet: 4,
        baslatmaKodu: ['(MACH3 G-CODE)', 'G90 G17 G40 G49 G80', 'G21'],
        bitisKodu: ['M5', 'G0 Z25', 'G0 X0 Y0', 'M30'],
        satirNumarasiBaslangic: 0,
        satirNumarasiArtis: 0,
        satirNumarasiKullan: false,
        yuzdeBaslik: false,
        programNumarasi: 0,
        spindleFormat: 'S{rpm} M3',
        modalKullan: true,
        absoluteMode: true,
        koordinatFormat: 'X{x} Y{y} Z{z}',
        feedFormat: 'F{feed:.0f}',
        yorumFormat: '({yorum})',
        coolantOn: 'M8',
        coolantOff: 'M9',
        toolChangeFormat: 'T{tool} M6',
        arcFormatAbsolute: false,
        safeZ: 25,
        homeX: 0,
        homeY: 0,
        homeZ: 50
    },
    'LinuxCNC': {
        isim: 'LinuxCNC/GRBL',
        uzanti: '.ngc',
        hassasiyet: 4,
        baslatmaKodu: ['(LinuxCNC)', 'G90 G17 G21 G40 G49 G54 G80'],
        bitisKodu: ['M5', 'G0 Z25', 'G0 X0 Y0', 'M2'],
        satirNumarasiBaslangic: 0,
        satirNumarasiArtis: 0,
        satirNumarasiKullan: false,
        yuzdeBaslik: false,
        programNumarasi: 0,
        spindleFormat: 'S{rpm} M3',
        modalKullan: true,
        absoluteMode: true,
        koordinatFormat: 'X{x} Y{y} Z{z}',
        feedFormat: 'F{feed:.1f}',
        yorumFormat: '({yorum})',
        coolantOn: 'M8',
        coolantOff: 'M9',
        toolChangeFormat: 'T{tool} M6',
        arcFormatAbsolute: false,
        safeZ: 10,
        homeX: 0,
        homeY: 0,
        homeZ: 25
    },
    'GRBL': {
        isim: 'GRBL',
        uzanti: '.gcode',
        hassasiyet: 3,
        baslatmaKodu: ['; GRBL G-CODE', 'G90 G17 G21', 'G54'],
        bitisKodu: ['M5', 'G0 Z10', 'G0 X0 Y0', 'M2'],
        satirNumarasiBaslangic: 0,
        satirNumarasiArtis: 0,
        satirNumarasiKullan: false,
        yuzdeBaslik: false,
        programNumarasi: 0,
        spindleFormat: 'S{rpm} M3',
        modalKullan: true,
        absoluteMode: true,
        koordinatFormat: 'X{x} Y{y} Z{z}',
        feedFormat: 'F{feed:.0f}',
        yorumFormat: ';{yorum}',
        coolantOn: 'M8',
        coolantOff: 'M9',
        toolChangeFormat: '; Tool {tool}',
        arcFormatAbsolute: false,
        safeZ: 10,
        homeX: 0,
        homeY: 0,
        homeZ: 20
    }
};

/**
 * G-Code Generator Ana Sınıfı
 */
export class FikoGCodeGenerator {
    private config: FikoPostProcessorConfig;
    private output: string[] = [];
    private lineNumber: number = 0;
    private lastG: string = '';
    private lastF: number = 0;
    private lastS: number = 0;
    private lastX: number = NaN;
    private lastY: number = NaN;
    private lastZ: number = NaN;

    constructor(postProcessor: string = 'Fanuc') {
        // Varsayılan konfigürasyon
        this.config = {
            isim: 'Generic',
            uzanti: '.nc',
            hassasiyet: 4,
            baslatmaKodu: ['G90 G21'],
            bitisKodu: ['M30'],
            satirNumarasiBaslangic: 10,
            satirNumarasiArtis: 10,
            satirNumarasiKullan: false,
            yuzdeBaslik: false,
            programNumarasi: 1,
            spindleFormat: 'S{rpm} M3',
            modalKullan: true,
            absoluteMode: true,
            koordinatFormat: 'X{x} Y{y} Z{z}',
            feedFormat: 'F{feed:.0f}',
            yorumFormat: '({yorum})',
            coolantOn: 'M8',
            coolantOff: 'M9',
            toolChangeFormat: 'T{tool} M6',
            arcFormatAbsolute: false,
            safeZ: 50,
            homeX: 0,
            homeY: 0,
            homeZ: 100
        };

        // Preset uygula
        if (POST_PROCESSOR_PRESETS[postProcessor]) {
            Object.assign(this.config, POST_PROCESSOR_PRESETS[postProcessor]);
        }
    }

    /**
     * Konfigürasyonu güncelle
     */
    setConfig(config: Partial<FikoPostProcessorConfig>): void {
        Object.assign(this.config, config);
    }

    /**
     * Post-processor seç
     */
    setPostProcessor(name: string): void {
        if (POST_PROCESSOR_PRESETS[name]) {
            Object.assign(this.config, POST_PROCESSOR_PRESETS[name]);
        }
    }

    /**
     * FikoTakimYolu'ndan G-Code üret
     */
    generateFromTakimYolu(takimYolu: FikoTakimYolu, programAdi?: string): string {
        return this.generate(takimYolu.noktalar, programAdi || takimYolu.name);
    }

    /**
     * Takım yolu noktalarından G-Code üret
     */
    generate(noktalar: FikoVektorTakimYol3D[], programAdi: string = 'FIKOYT'): string {
        this.output = [];
        this.lineNumber = this.config.satirNumarasiBaslangic;
        this.lastG = '';
        this.lastF = 0;
        this.lastS = 0;
        this.lastX = NaN;
        this.lastY = NaN;
        this.lastZ = NaN;

        // Başlık
        this.addHeader(programAdi);

        // Başlatma kodları
        this.addStartupCodes(programAdi);

        // İlk takım ve spindle
        if (noktalar.length > 0 && noktalar[0].takim) {
            this.addToolChange(1, noktalar[0].takim);
            if (noktalar[0].devir) {
                this.addSpindle(noktalar[0].devir);
            }
        }

        // Coolant açık
        this.addLine(this.config.coolantOn);

        // Takım yolu noktalarını işle
        let currentDevir = noktalar[0]?.devir || 0;
        let currentTakim = noktalar[0]?.takim;

        for (let i = 0; i < noktalar.length; i++) {
            const nokta = noktalar[i];

            // Takım değişikliği kontrolü
            if (nokta.takim && nokta.takim !== currentTakim) {
                this.addComment(`Tool Change: ${nokta.takim.cap}mm`);
                this.addLine(this.config.coolantOff);
                this.addToolChange(i + 1, nokta.takim);
                this.addLine(this.config.coolantOn);
                currentTakim = nokta.takim;
            }

            // Devir değişikliği kontrolü
            if (nokta.devir && nokta.devir !== currentDevir) {
                this.addSpindle(nokta.devir);
                currentDevir = nokta.devir;
            }

            // Hareket tipine göre G-Code üret
            this.addMove(nokta);
        }

        // Coolant kapalı
        this.addLine(this.config.coolantOff);

        // Bitiş kodları
        this.addEndCodes(programAdi);

        return this.output.join('\n');
    }

    /**
     * Program başlığı ekle
     */
    private addHeader(programAdi: string): void {
        if (this.config.yuzdeBaslik) {
            this.output.push('%');
        }

        // Program numarası
        if (this.config.programNumarasi > 0) {
            const oLine = `O${this.config.programNumarasi.toString().padStart(4, '0')} (${programAdi})`;
            this.output.push(oLine);
        }

        // Tarih ve saat
        const now = new Date();
        this.addComment(`Generated by FikoYT CAM - ${now.toISOString()}`);
        this.addComment(`Program: ${programAdi}`);
    }

    /**
     * Başlatma kodlarını ekle
     */
    private addStartupCodes(programAdi: string): void {
        for (const line of this.config.baslatmaKodu) {
            const processedLine = line
                .replace('{programNo}', this.config.programNumarasi.toString().padStart(4, '0'))
                .replace('{programAdi}', programAdi);
            this.addLine(processedLine);
        }
    }

    /**
     * Bitiş kodlarını ekle
     */
    private addEndCodes(programAdi: string): void {
        for (const line of this.config.bitisKodu) {
            const processedLine = line.replace('{programAdi}', programAdi);
            this.addLine(processedLine);
        }
    }

    /**
     * Takım değişikliği
     */
    private addToolChange(toolNumber: number, takim: any): void {
        const takimAdi = takim ? `D${takim.cap}` : `T${toolNumber}`;
        const line = this.config.toolChangeFormat
            .replace('{tool}', toolNumber.toString())
            .replace('{takimAdi}', takimAdi)
            .replace('{rpm}', (takim?.devir || 3000).toString());
        this.addLine(line);
    }

    /**
     * Spindle başlat
     */
    private addSpindle(rpm: number): void {
        if (rpm !== this.lastS) {
            const line = this.config.spindleFormat.replace('{rpm}', rpm.toString());
            this.addLine(line);
            this.lastS = rpm;
        }
    }

    /**
     * Hareket komutu ekle
     */
    private addMove(nokta: FikoVektorTakimYol3D): void {
        let gCode = '';
        let coords = '';
        let feed = '';
        let arcParams = '';

        // G kodu belirle
        switch (nokta.tip) {
            case 'rapid':
                gCode = 'G0';
                break;
            case 'linear':
            case 'cut':
            case 'feed':
                gCode = 'G1';
                break;
            case 'arc_cw':
                gCode = 'G2';
                break;
            case 'arc_ccw':
                gCode = 'G3';
                break;
            case 'dwell':
                this.addLine(`G4 P${nokta.dwell || 1}`);
                return;
            default:
                gCode = 'G1';
        }

        // Modal kullanımı: Aynı G kodu tekrarlama
        const showG = !this.config.modalKullan || gCode !== this.lastG;

        // Koordinatlar - sadece değişenleri göster
        const x = this.formatNumber(nokta.x);
        const y = this.formatNumber(nokta.y);
        const z = this.formatNumber(nokta.z);

        const parts: string[] = [];

        if (showG) parts.push(gCode);

        // X koordinatı
        if (nokta.x !== this.lastX || !this.config.modalKullan) {
            parts.push(`X${x}`);
            this.lastX = nokta.x;
        }

        // Y koordinatı
        if (nokta.y !== this.lastY || !this.config.modalKullan) {
            parts.push(`Y${y}`);
            this.lastY = nokta.y;
        }

        // Z koordinatı
        if (nokta.z !== this.lastZ || !this.config.modalKullan) {
            parts.push(`Z${z}`);
            this.lastZ = nokta.z;
        }

        // Arc parametreleri (G2/G3)
        if (gCode === 'G2' || gCode === 'G3') {
            if (nokta.i !== undefined && nokta.i !== 0) {
                parts.push(`I${this.formatNumber(nokta.i)}`);
            }
            if (nokta.j !== undefined && nokta.j !== 0) {
                parts.push(`J${this.formatNumber(nokta.j)}`);
            }
            if (nokta.k !== undefined && nokta.k !== 0) {
                parts.push(`K${this.formatNumber(nokta.k)}`);
            }
        }

        // Feed rate - sadece değiştiğinde ve rapid değilse
        if (gCode !== 'G0') {
            const feedRate = nokta.ilerleme || nokta.hiz || 1000;
            if (feedRate !== this.lastF) {
                parts.push(`F${Math.round(feedRate)}`);
                this.lastF = feedRate;
            }
        }

        // Satır oluştur
        if (parts.length > 0) {
            this.addLine(parts.join(' '));
            this.lastG = gCode;
        }
    }

    /**
     * Yorum satırı ekle
     */
    private addComment(yorum: string): void {
        const line = this.config.yorumFormat.replace('{yorum}', yorum);
        this.output.push(line);
    }

    /**
     * Satır ekle (numara ile)
     */
    private addLine(content: string): void {
        if (this.config.satirNumarasiKullan) {
            this.output.push(`N${this.lineNumber} ${content}`);
            this.lineNumber += this.config.satirNumarasiArtis;
        } else {
            this.output.push(content);
        }
    }

    /**
     * Sayı formatla
     */
    private formatNumber(value: number): string {
        if (value === undefined || value === null || isNaN(value)) {
            return '0';
        }
        return value.toFixed(this.config.hassasiyet);
    }

    /**
     * Mevcut G-Code çıktısını al
     */
    getOutput(): string {
        return this.output.join('\n');
    }

    /**
     * Dosya olarak indir
     */
    downloadGCode(programAdi: string = 'program'): void {
        const content = this.getOutput();
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${programAdi}${this.config.uzanti}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * Tahmini işleme süresini hesapla (dakika)
     */
    calculateMachiningTime(noktalar: FikoVektorTakimYol3D[]): number {
        let totalTime = 0;

        for (let i = 1; i < noktalar.length; i++) {
            const prev = noktalar[i - 1];
            const curr = noktalar[i];

            // Mesafe hesapla
            const dx = curr.x - prev.x;
            const dy = curr.y - prev.y;
            const dz = curr.z - prev.z;
            const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

            // Hız al (mm/dk)
            const feed = curr.tip === 'rapid' ? 10000 : (curr.ilerleme || curr.hiz || 1000);

            // Süre hesapla (dakika)
            totalTime += distance / feed;
        }

        return totalTime;
    }

    /**
     * G-Code istatistiklerini al
     */
    getStatistics(noktalar: FikoVektorTakimYol3D[]): {
        toplamHareket: number;
        rapidHareket: number;
        kesimHareket: number;
        yayHareket: number;
        toplamMesafe: number;
        kesimMesafesi: number;
        rapidMesafesi: number;
        tahminSure: number;
    } {
        let rapidCount = 0;
        let cutCount = 0;
        let arcCount = 0;
        let rapidDistance = 0;
        let cutDistance = 0;

        for (let i = 1; i < noktalar.length; i++) {
            const prev = noktalar[i - 1];
            const curr = noktalar[i];

            const dx = curr.x - prev.x;
            const dy = curr.y - prev.y;
            const dz = curr.z - prev.z;
            const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

            switch (curr.tip) {
                case 'rapid':
                    rapidCount++;
                    rapidDistance += distance;
                    break;
                case 'arc_cw':
                case 'arc_ccw':
                    arcCount++;
                    cutDistance += distance;
                    break;
                default:
                    cutCount++;
                    cutDistance += distance;
            }
        }

        return {
            toplamHareket: noktalar.length,
            rapidHareket: rapidCount,
            kesimHareket: cutCount,
            yayHareket: arcCount,
            toplamMesafe: rapidDistance + cutDistance,
            kesimMesafesi: cutDistance,
            rapidMesafesi: rapidDistance,
            tahminSure: this.calculateMachiningTime(noktalar)
        };
    }

    /**
     * Desteklenen post-processor listesi
     */
    static getAvailablePostProcessors(): string[] {
        return Object.keys(POST_PROCESSOR_PRESETS);
    }
}

// Default export
export default FikoGCodeGenerator;
